import React, { useEffect, useState, memo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';

//external packages
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect, useDispatch } from 'react-redux';
import styled from 'styled-components';

//internal ui packages
import { store } from 'redux-rest-reducer';
import { ILogger, USER_TYPES, CALL_TYPE, CueLogger } from '@cuemath/intel';

import SpeedTestHandler from 'modules/speed-test';
import { trackEvent } from 'modules/analytics';

import { DATA_EVENT_TYPE } from 'constants/logger';
import { PRE_CLASS_SCREEN_MAX_TIME } from 'constants/av-player';

//actions
import { get as getTeacherClassStatus } from 'actions/teacher-class-status';

//components
import PreClassScreen from './pre-class-screen';
import CallScreen from './call-screen';
import FallbackScreen from './fallback-screen';
import { useDemoClassroomPatch } from '../../actions/studentsv2';

const Wrapper = styled.div``;

const AVCallContainer = props => {
  const {
    actions,
    classStatus,
    classStatusFailed,
    classStatusLoading,
    classStatusLoaded,
    teacherClassroomId,
    userId,
  } = props;
  const [isClassJoined, setIsClassJoined] = useState(false);

  const dispatch = useDispatch();

  const { patch: patchClassroom } = useDemoClassroomPatch({
    onComplete: () => setIsClassJoined(true),
  });

  useEffect(() => {
    actions.getTeacherClassStatus(teacherClassroomId);
  }, [actions, teacherClassroomId]);

  const {
    class_start_delta_time: deltaStartTime,
    scheduled_end_datetime: scheduledEndTime,
    rm_call_enabled: isRMCallEnabled,
    class_type: classType,
    students = [],
  } = classStatus || {};

  const { student_classroom_id: studentClassroomId } = students?.[0] || {};

  const metricsDataRef = useRef({
    callId: null,
    userCallId: null,
  });

  const onEnterClass = () => {
    if (classType === 'DEMO') {
      dispatch(
        patchClassroom(studentClassroomId, {
          action: 'AC_ENTER',
        }),
      );
    } else {
      setIsClassJoined(true);
    }
  };

  const onEndClass = () => {
    actions.getTeacherClassStatus(teacherClassroomId);
    if (isMeetingOver()) setIsClassJoined(false);
  };

  const isMeetingOver = useCallback(() => {
    return scheduledEndTime <= new Date().getTime() / 1000;
  }, [scheduledEndTime]);

  const metricLogger = useCallback(
    (event, data) => {
      if (!classType || !teacherClassroomId) {
        return;
      }

      const {
        call_type_intent_id: prevCallId,
        user_call_type_intent_id: prevUserCallId,
      } = data;

      if (prevCallId) {
        metricsDataRef.current.callId = prevCallId;
        metricsDataRef.current.userCallId = prevUserCallId;
      }

      const { callId, userCallId } = metricsDataRef.current || {};

      const payload = {
        ...(data || {}),
        call_type: CALL_TYPE.GROUP_CALL,
        call_type_intent_id: callId,
        class_type: classType,
        level: ILogger.LOG_LEVELS.DATA,
        step: event,
        teacher_classroom_id: teacherClassroomId,
        user_call_type_intent_id: userCallId,
        user_id: userId,
        user_type: USER_TYPES.AC,
      };

      CueLogger.table(payload);
      trackEvent(DATA_EVENT_TYPE.AV_METRIC, payload);
    },
    [userId, teacherClassroomId, classType],
  );

  const classStatusNotLoaded = classStatusLoading || classStatusFailed;

  const showFallback =
    classStatusNotLoaded ||
    !isRMCallEnabled ||
    !deltaStartTime ||
    deltaStartTime > PRE_CLASS_SCREEN_MAX_TIME ||
    isMeetingOver();

  return (
    <Wrapper>
      {isRMCallEnabled &&
        !isClassJoined &&
        !isMeetingOver() &&
        deltaStartTime < PRE_CLASS_SCREEN_MAX_TIME && (
          <PreClassScreen
            classStatus={classStatus}
            onEnterClass={onEnterClass}
            metricLogger={metricLogger}
            userId={userId}
          />
        )}
      {isClassJoined && !isMeetingOver() && (
        <CallScreen
          teacherClassroomId={teacherClassroomId}
          classStatus={classStatus}
          onEndClass={onEndClass}
          userId={userId}
        />
      )}
      {showFallback && (
        <FallbackScreen
          classStatus={classStatus}
          classStatusFailed={classStatusFailed}
          classStatusLoading={classStatusLoading}
          teacherClassroomId={teacherClassroomId}
        />
      )}
      {classStatusLoaded && (
        <SpeedTestHandler teacherClassroomId={teacherClassroomId} userId={userId} />
      )}
    </Wrapper>
  );
};

AVCallContainer.propTypes = {
  classStatus: PropTypes.shape({}),
  classStatusFailed: PropTypes.bool,
  classStatusLoading: PropTypes.bool,
  classStatusLoaded: PropTypes.bool,
  teacherClassroomId: PropTypes.string,
  actions: PropTypes.shape({}),
  userId: PropTypes.string.isRequired,
};

AVCallContainer.defaultProps = {
  classStatus: {},
  classStatusFailed: null,
  classStatusLoading: null,
  classStatusLoaded: null,

  teacherClassroomId: '',
  actions: null,
};

export default withRouter(
  connect(
    (
      { teacherClassStatus, auth: { userId } = {} },
      { match: { params: { teacherClassroomId } } = {} },
    ) => {
      const teacherClassStatusData = store.get(
        teacherClassStatus,
        `${teacherClassroomId || ''}`,
      );

      const {
        data: classStatus,
        isLoadingFailed: classStatusFailed,
        isLoading: classStatusLoading,
        isLoaded: classStatusLoaded,
      } = teacherClassStatusData || {};

      return {
        classStatus,
        classStatusFailed,
        classStatusLoading,
        classStatusLoaded,
        teacherClassroomId,
        userId,
      };
    },
    dispatch => ({
      actions: bindActionCreators({ getTeacherClassStatus }, dispatch),
    }),
  )(memo(AVCallContainer)),
);
